import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faCircleCheck,
  faCircleExclamation,
  faCircleXmark,
  faHourglassHalf,
  faPauseCircle,
} from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { useEffect, useState } from "react";
import axiosInstance from "../../utils/axiosInstance";
import GetUser from "../../utils/getUser";
import UseGraph from "../../utils/useGraph";
import formQueryUrl from "../../utils/formQueryUrl";
import { safePercent } from "../../utils/format-number";
import StatNumber from "./stat-number";
import { SkeletonStatTile } from "./skeleton";

type Tile = {
  status: string;
  label: string;
  icon: IconDefinition;
  /** Icon ink. Status colours are reserved for state and never reused as series hues. */
  tone: string;
  /** Icon plate. */
  plate: string;
  /** Share meter fill. */
  bar: string;
};

const TILES: Tile[] = [
  {
    status: "Awaiting-Customer",
    label: "Awaiting customer",
    icon: faHourglassHalf,
    tone: "text-amber-500",
    plate: "bg-amber-500/10 border-amber-500/20",
    bar: "bg-amber-500",
  },
  {
    status: "On-Hold",
    label: "On hold",
    icon: faPauseCircle,
    tone: "text-indigo-500",
    plate: "bg-indigo-500/10 border-indigo-500/20",
    bar: "bg-indigo-500",
  },
  {
    status: "Debt-Collectors",
    label: "With collectors",
    icon: faCircleExclamation,
    tone: "text-sky-500",
    plate: "bg-sky-500/10 border-sky-500/20",
    bar: "bg-sky-500",
  },
  {
    status: "Collected",
    label: "Collected",
    icon: faCircleCheck,
    tone: "text-emerald-500",
    plate: "bg-emerald-500/10 border-emerald-500/20",
    bar: "bg-emerald-500",
  },
  {
    status: "Failed",
    label: "Failed",
    icon: faCircleXmark,
    tone: "text-orange-500",
    plate: "bg-orange-500/10 border-orange-500/20",
    bar: "bg-orange-500",
  },
  {
    status: "Chargebacks",
    label: "Chargebacks",
    icon: faBan,
    tone: "text-rose-500",
    plate: "bg-rose-500/10 border-rose-500/20",
    bar: "bg-rose-500",
  },
];

/**
 * The real-time KPI row: six status counts in a 3 × 2 grid.
 *
 * A KPI row of stat tiles, not a bar chart — these are six headline numbers, and a
 * chart would make the reader decode geometry to recover a figure that fits in four
 * characters. The thin meter under each value carries share-of-total, which is the
 * one comparison a flat number can't make.
 *
 * Dashboard-only copy: TopDashboardWidget is still used by ChaseList, so it is
 * deliberately left alone.
 */
export default function StatTiles() {
  const user = GetUser();
  const [loading, setLoading] = useState<boolean>(true);
  const [counts, setCounts] = useState<Record<string, any>[]>([]);
  const isMyCustomer = window.location.pathname === "/my-customers";
  const { startDate, endDate, isRestarted } = UseGraph();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      const filtersArray = [
        isRestarted && { restarted: true },
        isMyCustomer && { myCustomer: user?.code },
        startDate !== null && { startDate },
        endDate !== null && { endDate },
      ];
      try {
        const res = await axiosInstance.get(
          `/stats/chase/counts${formQueryUrl(filtersArray, "?")}`
        );
        if (!cancelled) setCounts(res.data);
      } catch (error) {
        console.log(error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [user?.code, isMyCustomer, startDate, endDate, isRestarted]);

  const total = counts.reduce((sum, c) => sum + Number(c?.count || 0), 0);

  if (loading) {
    return (
      <div className="grid grid-cols-3 gap-4 smallLaptop:grid-cols-2 mobile:grid-cols-1">
        {TILES.map((t) => (
          <SkeletonStatTile key={t.status} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-4 smallLaptop:grid-cols-2 mobile:grid-cols-1">
      {TILES.map((tile) => {
        const count = Number(
          counts.find((c) => c.status === tile.status)?.count ?? 0
        );
        const share = safePercent(count, total);

        return (
          <article
            key={tile.status}
            className="group rounded-2xl border border-edge bg-surface p-5 shadow-card transition-all duration-300 hover:border-edge-strong hover:-translate-y-0.5"
          >
            <div className="flex items-center gap-4">
              <div
                className={`w-11 h-11 rounded-xl border flex items-center justify-center shrink-0 ${tile.plate}`}
              >
                <FontAwesomeIcon
                  icon={tile.icon}
                  className={`text-[17px] ${tile.tone}`}
                />
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-medium text-light-gray truncate">
                  {tile.label}
                </p>
                {/* Ink, not the status hue — the coloured plate beside it carries identity. */}
                <p className="mt-0.5 text-[26px] font-bold text-text-primary leading-none">
                  <StatNumber value={count} unit="chases" />
                </p>
              </div>

              <div className="text-right shrink-0">
                <span className="text-[12px] font-semibold text-text-secondary">
                  {share.toFixed(0)}%
                </span>
              </div>
            </div>

            {/* Share of all chases. Track is a neutral step so the fill reads as the value. */}
            <div className="mt-4 h-1 w-full rounded-full bg-overlay-mid overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${tile.bar}`}
                style={{ width: `${share}%` }}
              />
            </div>
          </article>
        );
      })}
    </div>
  );
}
